import React, { useContext } from 'react';
import { TModule, TProductCarousel, TProductList } from '@com/core/common/types/catalog';
import Widgets from '@fastfishio/widgets/lib';
import DynamicWidget from '@com/core/common/components/dynamicWidget';
import { AppContext } from '@com/core/common/contexts/appContext';
import { AdContext } from '@com/core/common/contexts/adContext';
import Analytics from '@com/core/common/utils/analytics';
import FulfilmentBadge from '@com/core/common/components/fulfilment/FulfilmentBadge';
import { MARKETPLACE } from '../../../common/baseConfig';
import LimitedDealBar from '../limitedDealBar/LimitedDealBar';
import { NavContext } from '../../../common/contexts/navContext';
import { CartContext } from '../../../common/contexts/cartContext';
import { CustomerContext } from '../../../common/contexts/customerContext';
import { getMarketplaceConfig } from '../../../common/utils/config';
import CatalogTag from '../catalogTag/CatalogTag';
import Price from '../price/Price';
import { useTranslation } from '../../../common/translations/i18n';
import FlipCardTimer from '../countdownTimer/FlipCardTimer';
import { RecommendationContext } from '../../../common/contexts/recommendationContext';

// eslint-disable-next-line no-shadow
enum CATALOG_WIDGETS_MAP {
    'bannerSlider' = 'banner_slider',
    'adBannerSlider' = 'ad_banner_slider',
    'bannerModule' = 'banner_module',
    'bannerModuleGroup' = 'banner_module_group',
    'adBannerModule' = 'ad_banner_module',
    'bannerModuleCarousel' = 'banner_module_carousel',
    'bannerModuleOverlay' = 'banner_module_overlay',
    'adBannerModuleCarousel' = 'ad_banner_module_carousel',
    'bannerModuleStrip' = 'banner_module_strip',
    'bannerModuleStripTimer' = 'banner_module_strip_timer',
    'adBannerModuleStrip' = 'ad_banner_module_strip',
    // 'heroProductList' = 'listing',
    'videoModule' = 'video_module',
    'externalVideoModule' = 'external_video_module',
    'textLinks' = 'text_links',
    'productCarousel' = 'product_carousel',
    'recommendedProducts' = 'product_carousel',
    'productList' = 'product_listing',
    // 'staticNavigation' = 'static_navigation',
    // 'productHighlight' = 'listing'
}

const getParameterByName = (name: string, url?: string): string => {
    if (!url) return null;
    const trimmedName = name.replace(/[[\]]/g, '\\$&');
    const regex = new RegExp(`[?&]${trimmedName}(=([^&#]*)|&|#|$)`);
    const results = regex.exec(url);
    if (!results) return null;
    if (!results[2]) return '';
    return decodeURIComponent(results[2].replace(/\+/g, ' '));
};

type TProps = {
    component: TModule;
    viewType: 'mobile' | 'desktop';
    usage?: string;
};

const adModules = ['adBannerSlider', 'adBannerModule', 'adBannerModuleCarousel', 'adBannerModuleStrip'];

const StaticComponent = ({ component, usage, viewType }: TProps): JSX.Element | null => {
    const { type } = component;
    const { locale, webConfig } = useContext(AppContext);
    const { t } = useTranslation('common');

    const { recommendedSkus } = useContext(RecommendationContext);

    const customerContext = useContext(CustomerContext);
    const cartContext = useContext(CartContext);
    const navContext = useContext(NavContext);
    const adContext = useContext(AdContext);

    const renderCatalogTag = ({ product }) => <CatalogTag product={product} />;
    const renderFulfilmentBox = ({ product }) => <FulfilmentBadge product={product} />;
    const renderPrice = ({ decimals, price, salePrice }) => (
        <Price price={price} salePrice={salePrice} decimals={decimals} />
    );
    const renderLimitedDealBar = ({ expiry, stockGross, stockNet }) => (
        <LimitedDealBar expiry={expiry} stockGross={stockGross} stockNet={stockNet} />
    );
    const renderFrenzyDealBar = ({ expiry }) => <FlipCardTimer endTime={expiry} hideDays />;

    const Widget = Widgets.getWidget(CATALOG_WIDGETS_MAP[type], viewType) as (props: any) => JSX.Element;

    if (['productCarousel', 'recommendedProducts'].includes(type)) {
        const moduleData = component as TProductCarousel;

        if (type === 'recommendedProducts') {
            return recommendedSkus?.length > 0 ? (
                <DynamicWidget productSkus={recommendedSkus}>
                    {(data, isLoading): JSX.Element | null => {
                        if (!Array.isArray(data)) return null;
                        return (
                            <Widget
                                products={isLoading ? [1, 2, 3, 4, 5] : data}
                                moduleHeader={moduleData.moduleHeader}
                                textColor={moduleData.textColor}
                                usage={usage}
                                productBox={{
                                    locale,
                                    renderCatalogTag,
                                    renderFulfilmentBox,
                                    renderLimitedDealBar,
                                    renderFrenzyDealBar,
                                    renderPrice,
                                    cartContext,
                                    customerContext,
                                    appConfig: getMarketplaceConfig(MARKETPLACE),
                                    navContext,
                                    t,
                                }}
                            />
                        );
                    }}
                </DynamicWidget>
            ) : // TODO - instead of null show a placeholder?
            null;
        }

        return (
            <DynamicWidget
                productUrl={moduleData.productUrl}
                limit={Number(getParameterByName('limit', moduleData.productUrl)) || 0}>
                {(data, isLoading): JSX.Element | null => {
                    if (!Array.isArray(data)) return null;
                    return (
                        <Widget
                            products={isLoading ? [1, 2, 3, 4, 5] : data}
                            moduleHeader={moduleData.moduleHeader}
                            textColor={moduleData.textColor}
                            usage={usage}
                            productBox={{
                                locale,
                                renderCatalogTag,
                                renderFulfilmentBox,
                                renderLimitedDealBar,
                                renderFrenzyDealBar,
                                renderPrice,
                                cartContext,
                                customerContext,
                                appConfig: getMarketplaceConfig(MARKETPLACE),
                                navContext,
                                t,
                            }}
                        />
                    );
                }}
            </DynamicWidget>
        );
    }

    if (type === 'productList') {
        const moduleData = component as TProductList;

        return (
            <DynamicWidget
                productUrl={moduleData.productUrl}
                limit={Number(getParameterByName('limit', moduleData.productUrl)) || 0}>
                {(data, isLoading): JSX.Element | null => {
                    if (!Array.isArray(data)) return null;
                    return (
                        <Widget
                            moduleData={{ hits: isLoading ? [1, 2, 3, 4, 5, 6] : data, ...moduleData }}
                            productBox={{
                                locale,
                                renderCatalogTag,
                                renderFulfilmentBox,
                                renderLimitedDealBar,
                                renderFrenzyDealBar,
                                renderPrice,
                                cartContext,
                                customerContext,
                                appConfig: getMarketplaceConfig(MARKETPLACE),
                                navContext,
                                t,
                            }}
                            showMoreLabel={t('show-more')}
                            showAllLabel={t('view-all-offers')}
                        />
                    );
                }}
            </DynamicWidget>
        );
    }

    if (adModules.includes(type)) {
        return (
            <Widget
                moduleData={component}
                cdnTemplate={webConfig.cdn['cms-v1']}
                isoLocale={locale.isoLocale}
                apiLocale={locale.apiLocale}
                adContext={adContext}
                tracking={Analytics.trackClick}
            />
        );
    }

    // Allow ATC via banner modules
    if (type === 'bannerModule' || type === 'bannerModuleGroup') {
        return (
            <Widget
                moduleData={component}
                cdnTemplate={webConfig.cdn['cms-v1']}
                isoLocale={locale.isoLocale}
                cartContext={cartContext}
            />
        );
    }

    if (Widget) {
        return (
            <Widget
                moduleData={component}
                cdnTemplate={webConfig.cdn['cms-v1']}
                isoLocale={locale.isoLocale}
                apiLocale={locale.apiLocale}
                tracking={Analytics.trackClick}
            />
        );
    }
    return null;
};

export default StaticComponent;
